import React, { useMemo } from "react";
import PropTypes from "prop-types";

import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import { IoTime } from "react-icons/io5";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { formatDate } from "../../helpers/date";

import ProjectProgressBar, {
  getProjectClassName
} from "../project/ProjectProgressBar";

const CallApprovalProgressBarDisplay = ({
  currentMonthlySegment,
  project,
  auth,
  uniqueId
}) => {
  const percentage =
    (currentMonthlySegment.consumed / currentMonthlySegment.budgetHours) * 100;

  const projectClassName = getProjectClassName(percentage);

  const billableHoursBalance =
    currentMonthlySegment.budgetHours - currentMonthlySegment.consumed || 0;

  return (
    <React.Fragment>
      <a
        id={uniqueId}
        data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
        href={`/app/projects/${project._id}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        <IoTime className={`projectIcon ${projectClassName}`} />
      </a>
      <ReactTooltip
        anchorId={uniqueId}
        className={`custom-tooltip ${auth.isNightMode ? "dark" : "light"}`}
        place="left"
      >
        <p style={{ marginBottom: 5 }}>
          Month of:&nbsp;
          <b>{formatDate(new Date(currentMonthlySegment.startDate), "MMMM")}</b>
        </p>
        <p style={{ marginBottom: 5 }}>
          Budget Hours:&nbsp;
          <b>{parseFloat(currentMonthlySegment.budgetHours).toFixed(2)}</b>
        </p>
        <p style={{ marginBottom: 5 }}>
          Billable Hours:&nbsp;
          <b>
            {currentMonthlySegment.consumedBillable
              ? parseFloat(currentMonthlySegment.consumedBillable).toFixed(2)
              : 0}
          </b>
        </p>
        <p style={{ marginBottom: 5 }}>
          Actual Hours:&nbsp;
          <b>{parseFloat(currentMonthlySegment.consumed).toFixed(2)}</b>
        </p>
        <p style={{ marginBottom: 3 }}>
          Balance Hours:&nbsp;
          <b>{parseFloat(billableHoursBalance).toFixed(2)}</b>
        </p>
        <p style={{ marginBottom: 5, paddingTop: 3, borderTop: "1px solid" }}>
          Total Budget Hours:&nbsp;
          <b>{parseFloat(project.budget_hours).toFixed(2)}</b>
        </p>
        <p>
          Rate:&nbsp;<b>${project.rate || 0}</b>
        </p>
      </ReactTooltip>
    </React.Fragment>
  );
};

CallApprovalProgressBarDisplay.propTypes = {
  currentMonthlySegment: PropTypes.object,
  project: PropTypes.object,
  auth: PropTypes.object,
  uniqueId: PropTypes.string
};

const CallApprovalProgressBar = ({ project, auth, call }) => {
  const callDate = useMemo(() => {
    const { arrival_date } = call;
    if (arrival_date) {
      return new Date(arrival_date);
    }
    return new Date();
  }, [call]);

  const currentMonthlySegment = useMemo(
    () =>
      project.budget_segments?.find(
        (segment) =>
          callDate >= startOfDay(new Date(segment.startDate)) &&
          callDate <= endOfDay(new Date(segment.endDate))
      ),
    [project, callDate]
  );

  if (currentMonthlySegment) {
    return (
      <CallApprovalProgressBarDisplay
        auth={auth}
        currentMonthlySegment={currentMonthlySegment}
        project={project}
        uniqueId={call._id}
      />
    );
  }

  return (
    <ProjectProgressBar
      project={project}
      type="icon"
      auth={auth}
      uniqueId={call._id}
    />
  );
};

CallApprovalProgressBar.propTypes = {
  project: PropTypes.object,
  auth: PropTypes.object,
  call: PropTypes.object
};

CallApprovalProgressBar.defaultProps = {
  project: {},
  call: {}
};

export default CallApprovalProgressBar;
